package com.trevorwiebe.timesheet.admin.carrier.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectDragGesturesAfterLongPress
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.zIndex
import com.trevorwiebe.timesheet.calendar.domain.vibrate
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun CarriersScreen(
    viewModel: CarriersViewModel = koinViewModel(),
    onBack: () -> Unit
) {
    val state by viewModel.state.collectAsState()

    Scaffold(
        topBar = {
            TopBar(
                title = "Carriers",
                backIcon = { BackIcon() },
                onBack = onBack
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { viewModel.onEvent(CarriersEvent.OnAddCarrierClick) },
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary
            ) {
                Icon(Icons.Default.Add, contentDescription = "Add carrier")
            }
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        Column(
            modifier = Modifier
                .fillMaxSize()
        ) {
            CarriersFilterChipRow(
                activeFilters = state.activeFilters,
                onFilterToggled = { viewModel.onEvent(CarriersEvent.OnFilterToggled(it)) }
            )
            HorizontalDivider()
            if (state.isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                }
            } else {
                ReorderableCarrierList(
                    carriers = state.displayedCarriers,
                    reorderEnabled = state.reorderEnabled,
                    onCarrierClick = { viewModel.onEvent(CarriersEvent.OnEditCarrierClick(it)) },
                    onReorder = { viewModel.onEvent(CarriersEvent.OnReorderCarriers(it)) }
                )
            }
        }

        if (state.showAddEditDialog) {
            AddEditCarrierDialog(
                editing = state.editingCarrier,
                onDismiss = { viewModel.onEvent(CarriersEvent.OnDismissDialog) },
                onSave = { contactName, companyName, address, phone, email, notes, active ->
                    viewModel.onEvent(
                        CarriersEvent.OnSaveCarrier(
                            contactName, companyName, address, phone, email, notes, active
                        )
                    )
                },
                onDelete = { viewModel.onEvent(CarriersEvent.OnDeleteCarrier) },
                onRestore = { viewModel.onEvent(CarriersEvent.OnRestoreCarrier) }
            )
        }

        NativeDialog(
            visible = state.error != null,
            title = "Something went wrong",
            message = state.error ?: "",
            confirmText = "OK",
            onConfirm = { viewModel.onEvent(CarriersEvent.OnErrorShown) },
            onDismiss = { viewModel.onEvent(CarriersEvent.OnErrorShown) }
        )
        }
    }
}

/**
 * The carrier list with press-and-hold drag-to-reorder (only when [reorderEnabled] — the
 * ACTIVE-only filter view, so the reordered id list covers the whole visible set). While a
 * drag is in flight the list renders a local working copy; the dragged row follows the finger
 * and swaps with neighbors as its center crosses their midpoint.
 */
@Composable
private fun ReorderableCarrierList(
    carriers: List<Carrier>,
    reorderEnabled: Boolean,
    onCarrierClick: (Carrier) -> Unit,
    onReorder: (List<String>) -> Unit
) {
    val listState = rememberLazyListState()
    var workingList by remember { mutableStateOf<List<Carrier>?>(null) }
    var draggedId by remember { mutableStateOf<String?>(null) }
    var dragOffset by remember { mutableStateOf(0f) }

    fun endDrag(commit: Boolean) {
        val list = workingList
        if (commit && list != null) onReorder(list.map { it.id })
        workingList = null
        draggedId = null
        dragOffset = 0f
    }

    val displayed = workingList ?: carriers

    LazyColumn(state = listState, modifier = Modifier.fillMaxSize()) {
        items(displayed, key = { it.id }) { carrier ->
            val isDragged = carrier.id == draggedId
            CarrierRow(
                carrier = carrier,
                onClick = { onCarrierClick(carrier) },
                modifier = Modifier
                    .then(
                        if (isDragged) {
                            Modifier
                                .zIndex(1f)
                                .graphicsLayer { translationY = dragOffset }
                        } else Modifier.animateItem()
                    )
                    .then(
                        if (reorderEnabled) {
                            Modifier.pointerInput(carrier.id, carriers) {
                                detectDragGesturesAfterLongPress(
                                    onDragStart = {
                                        vibrate()
                                        workingList = carriers
                                        draggedId = carrier.id
                                        dragOffset = 0f
                                    },
                                    onDrag = { change, dragAmount ->
                                        change.consume()
                                        dragOffset += dragAmount.y
                                        val list = workingList ?: return@detectDragGesturesAfterLongPress
                                        val fromIndex = list.indexOfFirst { it.id == draggedId }
                                        if (fromIndex < 0) return@detectDragGesturesAfterLongPress
                                        val visible = listState.layoutInfo.visibleItemsInfo
                                        val draggedInfo = visible.firstOrNull { it.key == draggedId }
                                            ?: return@detectDragGesturesAfterLongPress
                                        val draggedCenter = draggedInfo.offset + dragOffset + draggedInfo.size / 2f
                                        val target = visible.firstOrNull { info ->
                                            info.key != draggedId &&
                                                draggedCenter > info.offset + info.size * 0.25f &&
                                                draggedCenter < info.offset + info.size * 0.75f
                                        } ?: return@detectDragGesturesAfterLongPress
                                        val toIndex = list.indexOfFirst { it.id == target.key }
                                        if (toIndex >= 0 && toIndex != fromIndex) {
                                            workingList = list.toMutableList()
                                                .apply { add(toIndex, removeAt(fromIndex)) }
                                            // The dragged row's base offset jumps to the target's
                                            // old slot; counter it so the row stays under the finger.
                                            dragOffset -= (target.offset - draggedInfo.offset)
                                        }
                                    },
                                    onDragEnd = { endDrag(commit = true) },
                                    onDragCancel = { endDrag(commit = false) }
                                )
                            }
                        } else Modifier
                    )
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CarriersFilterChipRow(
    activeFilters: Set<CarrierFilter>,
    onFilterToggled: (CarrierFilter) -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy((-8).dp)
    ) {
        Text(
            text = "Filter",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
        )
        CarrierFilter.entries.forEach { filter ->
            val selected = filter in activeFilters
            FilterChip(
                selected = selected,
                onClick = { onFilterToggled(filter) },
                label = { Text(filter.name.lowercase().replaceFirstChar { it.uppercase() }) },
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    borderColor = MaterialTheme.colorScheme.tertiary,
                    selected = selected
                ),
                colors = FilterChipDefaults.filterChipColors(
                    containerColor = MaterialTheme.colorScheme.background,
                    selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                    selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                )
            )
        }
    }
}

@Composable
private fun CarrierRow(
    carrier: Carrier,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = carrier.contactName.ifEmpty { "(no name)" },
                    fontSize = 16.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                val contactLine = listOf(carrier.companyName, carrier.phone, carrier.email)
                    .filter { it.isNotBlank() }
                    .joinToString(" · ")
                if (contactLine.isNotEmpty()) {
                    Text(
                        text = contactLine,
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
            when {
                carrier.deleted -> Text(
                    text = "Deleted",
                    color = MaterialTheme.colorScheme.error,
                    fontSize = 14.sp,
                    maxLines = 1
                )
                !carrier.active -> Text(
                    text = "Inactive",
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                    fontSize = 14.sp,
                    maxLines = 1
                )
            }
        }
        HorizontalDivider()
    }
}

@Composable
private fun AddEditCarrierDialog(
    editing: Carrier?,
    onDismiss: () -> Unit,
    onSave: (
        contactName: String,
        companyName: String,
        address: String,
        phone: String,
        email: String,
        notes: String,
        active: Boolean
    ) -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit
) {
    var contactName by remember { mutableStateOf(editing?.contactName ?: "") }
    var companyName by remember { mutableStateOf(editing?.companyName ?: "") }
    var address by remember { mutableStateOf(editing?.address ?: "") }
    var phone by remember { mutableStateOf(editing?.phone ?: "") }
    var email by remember { mutableStateOf(editing?.email ?: "") }
    var notes by remember { mutableStateOf(editing?.notes ?: "") }
    var active by remember { mutableStateOf(editing?.active ?: true) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }

    NativeDialog(
        visible = showDeleteConfirm,
        title = "Delete Carrier",
        message = "Are you sure you want to delete this carrier? It can be restored, and existing records will keep their reference.",
        confirmText = "Delete",
        onConfirm = onDelete,
        dismissText = "Cancel",
        onDismiss = { showDeleteConfirm = false }
    )

    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier.responsiveDialogWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = if (editing == null) "Add Carrier" else "Edit Carrier",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(16.dp))

                TimesheetTextField(
                    text = contactName,
                    label = "Contact Name",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { contactName = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = companyName,
                    label = "Company Name",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { companyName = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    modifier = Modifier.height(120.dp),
                    text = address,
                    label = "Address",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { address = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = phone,
                    label = "Phone",
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                    onTextChange = { phone = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = email,
                    label = "Email",
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                    onTextChange = { email = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    modifier = Modifier.height(120.dp),
                    text = notes,
                    label = "Notes",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                    onTextChange = { notes = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "Active",
                        fontSize = 16.sp,
                        modifier = Modifier.weight(1f)
                    )
                    Switch(
                        checked = active,
                        onCheckedChange = { active = it },
                        colors = SwitchDefaults.colors(
                            checkedTrackColor = MaterialTheme.colorScheme.tertiary
                        )
                    )
                }
                Spacer(modifier = Modifier.height(8.dp))

                validationError?.let { error ->
                    Text(
                        text = error,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(bottom = 8.dp)
                    )
                }

                val isDeleted = editing?.deleted == true
                DialogButtonRow(
                    confirmButtonText = "Save",
                    confirmEnabled = !isDeleted,
                    onConfirmClick = {
                        if (contactName.isBlank()) {
                            validationError = "Please enter Contact Name before saving."
                        } else if (phone.isNotBlank() && !Util.isValidUsPhone(phone.trim())) {
                            validationError = "Please enter a valid US phone number."
                        } else if (email.isNotBlank() && !Util.isValidEmail(email.trim())) {
                            validationError = "Please enter a valid email address."
                        } else {
                            validationError = null
                            onSave(
                                contactName.trim(),
                                companyName.trim(),
                                address.trim(),
                                phone.trim(),
                                email.trim(),
                                notes.trim(),
                                active
                            )
                        }
                    },
                    dismissButtonText = "Cancel",
                    onDismissClick = onDismiss,
                    neutralButtonText = when {
                        editing == null -> null
                        isDeleted -> "Restore"
                        else -> "Delete"
                    },
                    onNeutralClick = if (isDeleted) onRestore else { { showDeleteConfirm = true } }
                )
            }
        }
    }
}
